import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, RotateCcw } from "lucide-react";
import PageHead from "@/components/PageHead";
import SharedFooter from "@/components/SharedFooter";
import WeKitLogo from "@/components/WeKitLogo";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { loadCareersBundle, type CareerRow } from "@/data/careersBundle";
import { ARCHETYPE_TAGLINES_THEY } from "@/features/assessment-v2/results";
import {
  TASTER_ITEMS, scoreTaster, type TasterAnswers, type TasterResult,
} from "@/features/assessment-v2/taster";
import { readFreeQuizLead, saveFreeQuizLead, clearFreeQuizLead } from "@/lib/freeQuizLead";

type Stage = "signup" | "quiz" | "result";

/** Where this visitor came from: popup, free resources page, a shared link, or direct. */
function captureLeadSource(): string {
  const KEY = "wekit.freequiz.src";
  try {
    const q = new URLSearchParams(window.location.search);
    const src = q.get("src");
    let tag: string | null = null;
    if (src === "popup" || src === "free-resources") tag = src;
    else if (["utm_source", "utm_campaign", "ref", "referral", "gclid", "fbclid"].some((k) => q.has(k))) tag = "shared-link";
    else if (document.referrer && !document.referrer.startsWith(window.location.origin)) tag = "shared-link";
    if (tag) { sessionStorage.setItem(KEY, tag); return tag; }
    return sessionStorage.getItem(KEY) || "direct";
  } catch { return "direct"; }
}

const AGE_BANDS = [
  { id: "12-14", label: "12 to 14" },
  { id: "15-18", label: "15 to 18" },
  { id: "18+", label: "18 or older" },
  { id: "parent", label: "I'm a parent" },
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirrors the rule the server applies, so a number is never rejected silently.
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;

/** Three directions the leading archetype opens, one per family, way-in first. */
function pickDirections(rows: CareerRow[], primary: string): CareerRow[] {
  const matches = rows.filter(
    (r) => r.archetypes[0]?.name === primary && r.confidence !== "low",
  );
  const wayIn = (r: CareerRow) => /entry|junior/i.test(r.level);
  const ordered = [...matches].sort((a, b) => {
    if (wayIn(a) !== wayIn(b)) return wayIn(a) ? -1 : 1;
    return (b.archetypes[0]?.pct ?? 0) - (a.archetypes[0]?.pct ?? 0);
  });
  const picked: CareerRow[] = [];
  const families = new Set<string>();
  for (const row of ordered) {
    if (families.has(row.family)) continue;
    families.add(row.family);
    picked.push(row);
    if (picked.length === 3) break;
  }
  return picked;
}

export default function CareerQuiz() {
  const saved = useRef(readFreeQuizLead());
  const [stage, setStage] = useState<Stage>("signup");
  const [name, setName] = useState(saved.current?.name ?? "");
  const [email, setEmail] = useState(saved.current?.email ?? "");
  const [mobile, setMobile] = useState(saved.current?.mobile ?? "");
  const [ageBand, setAgeBand] = useState<string>(saved.current?.ageBand ?? "");
  const [known, setKnown] = useState(Boolean(saved.current));

  const leadSource = useRef<string>(typeof window !== "undefined" ? captureLeadSource() : "direct");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<TasterAnswers>({});
  const [result, setResult] = useState<TasterResult | null>(null);
  const [directions, setDirections] = useState<CareerRow[] | null>(null);
  const [sending, setSending] = useState(false);
  const [voice, setVoice] = useState<"you" | "parent">(
    saved.current?.ageBand === "parent" ? "parent" : "you",
  );

  const item = TASTER_ITEMS[step];
  const progress = Math.round((step / TASTER_ITEMS.length) * 100);

  const mobileValid = useMemo(
    () => mobile.trim() === "" || PHONE_RE.test(mobile.trim()),
    [mobile],
  );

  const canStart = useMemo(
    () => name.trim().length > 1 && EMAIL_RE.test(email.trim()) && !!ageBand && mobileValid,
    [name, email, ageBand, mobileValid],
  );

  useEffect(() => {
    if (stage === "result" && result) {
      loadCareersBundle()
        .then((b) => setDirections(pickDirections(b.rows, result.primary)))
        .catch(() => setDirections([]));
    }
  }, [stage, result]);

  function startQuiz() {
    if (!canStart) return;
    saveFreeQuizLead({
      name: name.trim(), email: email.trim().toLowerCase(),
      mobile: mobile.trim() || null, ageBand, magnet: "career-quiz",
    });
    if (ageBand === "parent") setVoice("parent");
    setStage("quiz");
  }

  async function choose(optionId: string) {
    const next = { ...answers, [item.id]: optionId };
    setAnswers(next);
    if (step + 1 < TASTER_ITEMS.length) {
      setStep(step + 1);
      return;
    }
    const scored = scoreTaster(next);
    setResult(scored);
    setStage("result");
    await submit(next, scored);
  }

  async function submit(finalAnswers: TasterAnswers, scored: TasterResult) {
    setSending(true);
    try {
      const bundle = await loadCareersBundle().catch(() => null);
      const dirs = bundle ? pickDirections(bundle.rows, scored.primary) : [];
      saveFreeQuizLead({
        name: name.trim(), email: email.trim().toLowerCase(),
        mobile: mobile.trim() || null, ageBand, magnet: "career-quiz",
        answers: finalAnswers, primary: scored.primary, secondary: scored.secondary,
      });
      const { error } = await supabase.functions.invoke("free-quiz-submit", {
        body: {
          name: name.trim(),
          email: email.trim().toLowerCase(),
          mobile: mobile.trim() || null,
          age_band: ageBand,
          magnet: "career-quiz",
          answers: finalAnswers,
          archetype_primary: scored.primary,
          archetype_secondary: scored.secondary,
          archetype_vector: scored.vector,
          directions: dirs.map((d) => d.name),
          source: leadSource.current,
          user_agent: navigator.userAgent,
          result_url: `${window.location.origin}/free/career-quiz`,
        },
      });
      if (error) throw error;
    } catch {
      toast({
        title: "Your snapshot is right here",
        description: "We couldn't email a copy just now. Nothing is lost — it's on this screen.",
      });
    } finally {
      setSending(false);
    }
  }

  function restart() {
    setAnswers({});
    setStep(0);
    setResult(null);
    setDirections(null);
    setStage("quiz");
  }

  return (
    <div className="relative isolate min-h-dvh bg-background">
      <PageHead
        title="Free Career Discovery Quiz | WeKIT™"
        description="Eight questions from our real career assessment. See how you naturally work today and three career directions worth exploring — free, in about three minutes."
        path="/free/career-quiz"
      />

      <header className="sticky top-0 z-40 border-b border-border/50 bg-background/85 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4">
          <Link to="/free" className="flex items-center gap-2.5" aria-label="Free resources">
            <WeKitLogo size="sm" clickable={false} />
            <span className="font-display text-base font-bold tracking-tight">WeKIT™</span>
          </Link>
          {stage === "quiz" && (
            <span className="ml-auto text-xs text-muted-foreground" aria-live="polite">
              Question {step + 1} of {TASTER_ITEMS.length}
            </span>
          )}
        </div>
        {stage === "quiz" && (
          <div
            className="h-1 bg-primary transition-all"
            style={{ width: `${Math.max(progress, 4)}%` }}
            role="progressbar"
            aria-valuenow={step + 1}
            aria-valuemin={1}
            aria-valuemax={TASTER_ITEMS.length}
            aria-label="Quiz progress"
          />
        )}
      </header>

      <main className="mx-auto w-full max-w-3xl px-4 pb-20 pt-8 lg:pt-12">
        {stage === "signup" && (
          <section>
            <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
              Free · about 3 minutes
            </p>
            <h1 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              Career Discovery Quiz
            </h1>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Eight questions, taken straight from our full assessment. There are no
              right answers and nothing to revise for — just say what's true today.
            </p>

            {known && (
              <div className="mt-6 rounded-2xl border border-border/60 bg-[hsl(var(--surface))] p-4">
                <p className="text-sm">
                  Welcome back, {name.split(" ")[0]}. We've kept your details, so you can go
                  straight in.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    clearFreeQuizLead();
                    setKnown(false);
                    setName(""); setEmail(""); setMobile(""); setAgeBand("");
                  }}
                  className="mt-2 min-h-11 text-sm font-medium text-primary underline"
                >
                  Not you? Use different details
                </button>
              </div>
            )}

            <form
              className="mt-8 space-y-5"
              onSubmit={(e) => { e.preventDefault(); startQuiz(); }}
            >
              <div>
                <label htmlFor="fq-name" className="block text-sm font-medium">Your name</label>
                <input
                  id="fq-name" value={name} onChange={(e) => setName(e.target.value)}
                  autoComplete="name" required maxLength={120}
                  className="mt-2 min-h-12 w-full rounded-2xl border border-border/60 bg-[hsl(var(--surface))] px-4 text-base outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="fq-email" className="block text-sm font-medium">Email</label>
                <input
                  id="fq-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email" required maxLength={255}
                  className="mt-2 min-h-12 w-full rounded-2xl border border-border/60 bg-[hsl(var(--surface))] px-4 text-base outline-none focus-visible:ring-2 focus-visible:ring-primary"
                />
                <p className="mt-2 text-xs text-muted-foreground">
                  We'll send your snapshot here so you can come back to it.
                </p>
              </div>
              <div>
                <label htmlFor="fq-mobile" className="block text-sm font-medium">
                  Mobile <span className="text-muted-foreground">(optional)</span>
                </label>
                <input
                  id="fq-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)}
                  autoComplete="tel" maxLength={20} inputMode="tel"
                  aria-invalid={!mobileValid}
                  aria-describedby="fq-mobile-help"
                  className={`mt-2 min-h-12 w-full rounded-2xl border bg-[hsl(var(--surface))] px-4 text-base outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    mobileValid ? "border-border/60" : "border-destructive"
                  }`}
                />
                <p
                  id="fq-mobile-help"
                  className={`mt-2 text-xs ${mobileValid ? "text-muted-foreground" : "text-destructive"}`}
                >
                  {mobileValid
                    ? "Leave this blank if you'd rather not share it."
                    : "Please enter a full number with country code, like +91 98765 43210 — or leave it blank."}
                </p>
              </div>
              <fieldset>
                <legend className="text-sm font-medium">Who's taking this?</legend>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {AGE_BANDS.map((b) => (
                    <button
                      key={b.id} type="button" onClick={() => setAgeBand(b.id)}
                      aria-pressed={ageBand === b.id}
                      className={`flex min-h-12 items-center justify-between rounded-2xl border px-4 text-sm transition-colors ${
                        ageBand === b.id
                          ? "border-primary bg-primary/12 text-primary"
                          : "border-border/60 hover:bg-muted/30"
                      }`}
                    >
                      {b.label}
                      {ageBand === b.id && <Check className="h-4 w-4" aria-hidden="true" />}
                    </button>
                  ))}
                </div>
              </fieldset>
              <button
                type="submit" disabled={!canStart}
                className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-50 sm:w-auto"
              >
                Start the quiz <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </form>
          </section>
        )}

        {stage === "quiz" && item && (
          <section>
            <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
              {item.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-2xl font-bold leading-snug tracking-tight sm:text-3xl">
              {item.question}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">{item.helper}</p>

            <div className="mt-7 grid gap-3">
              {item.options.map((o) => (
                <button
                  key={o.id} type="button" onClick={() => choose(o.id)}
                  className="flex min-h-[60px] items-center rounded-2xl border border-border/60 bg-[hsl(var(--surface))] px-5 py-3 text-left text-[15px] leading-snug transition-colors hover:border-primary/60 hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {o.label}
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                type="button" onClick={() => setStep(step - 1)}
                className="mt-6 inline-flex min-h-11 items-center gap-1.5 text-sm text-muted-foreground"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
              </button>
            )}
          </section>
        )}

        {stage === "result" && result && (
          <section>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
                Your snapshot
              </p>
              <div className="ml-auto flex rounded-full border border-border/60 p-0.5" role="group" aria-label="Wording">
                {(["you", "parent"] as const).map((v) => (
                  <button
                    key={v} type="button" onClick={() => setVoice(v)}
                    aria-pressed={voice === v}
                    className={`min-h-9 rounded-full px-3 text-xs font-medium ${
                      voice === v ? "bg-primary/15 text-primary" : "text-muted-foreground"
                    }`}
                  >
                    {v === "you" ? "For me" : "For a parent"}
                  </button>
                ))}
              </div>
            </div>

            <h1 className="mt-3 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
              {voice === "you"
                ? `Today, you lean towards ${result.primary}`
                : `Today, they lean towards ${result.primary}`}
            </h1>
            <p className="mt-3 text-base leading-relaxed text-muted-foreground">
              {voice === "you"
                ? `From your answers, ${result.primaryTagline}. ${result.secondary} comes a close second: ${result.secondaryTagline}.`
                : `From their answers, ${ARCHETYPE_TAGLINES_THEY[result.primary]}. ${result.secondary} comes a close second: ${ARCHETYPE_TAGLINES_THEY[result.secondary]}.`}
            </p>

            <div className="mt-8 rounded-3xl border border-border/60 bg-[hsl(var(--surface))] p-6">
              <h2 className="font-display text-lg font-semibold tracking-tight">
                How this came out
              </h2>
              <ul className="mt-4 space-y-2">
                {result.rows.slice(0, 4).map((r) => (
                  <li key={r.name} className="flex items-center gap-3">
                    <span className="w-24 shrink-0 text-sm">{r.name}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <span
                        className="block h-full rounded-full bg-primary"
                        style={{ width: `${r.share}%` }}
                      />
                    </span>
                    <span className="w-10 shrink-0 text-right text-xs text-muted-foreground">
                      {r.share}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6 rounded-3xl border border-border/60 p-6">
              <h2 className="font-display text-lg font-semibold tracking-tight">Why we said that</h2>
              <ul className="mt-4 space-y-3">
                {result.evidence.map((e) => (
                  <li key={e.question} className="text-sm">
                    <span className="block text-muted-foreground">{e.question}</span>
                    <span className="mt-0.5 block">{e.answer}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-muted-foreground">
                Eight questions is a starting point, not a verdict. It shows what came through
                today, and today can change.
              </p>
            </div>

            <div className="mt-6 rounded-3xl border border-border/60 p-6">
              <h2 className="font-display text-lg font-semibold tracking-tight">
                Directions worth a look
              </h2>
              {directions === null ? (
                <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Finding them…
                </p>
              ) : directions.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">
                  We'll open these up for you inside the full programme.
                </p>
              ) : (
                <ul className="mt-4 grid gap-3">
                  {directions.map((d) => (
                    <li key={d.ciId} className="rounded-2xl bg-[hsl(var(--surface))] p-4">
                      <span className="block text-sm font-semibold">{d.name}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {d.family} · {d.level}
                        {d.entryRequirement ? ` · way in: ${d.entryRequirement}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-4 text-xs text-muted-foreground">
                Three of many. The full programme looks at over 5,000 roles and explains
                every match it puts in front of you.
              </p>
            </div>

            <div className="mt-8 rounded-3xl border border-primary/40 bg-primary/8 p-6">
              <h2 className="font-display text-xl font-semibold tracking-tight">
                Want the whole picture?
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Career Clarity 360™ goes far past eight questions: how you work, what you can
                take, what you want, and the careers that genuinely fit — with the reasoning
                shown for each one, and a plan for what to do next.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/payment"
                  className="inline-flex min-h-12 items-center gap-2 rounded-2xl bg-primary px-5 text-sm font-semibold text-primary-foreground"
                >
                  See the full programme <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <button
                  type="button" onClick={restart}
                  className="inline-flex min-h-12 items-center gap-2 rounded-2xl border border-border/60 px-5 text-sm font-medium"
                >
                  <RotateCcw className="h-4 w-4" aria-hidden="true" /> Take it again
                </button>
              </div>
              {sending && (
                <p className="mt-4 text-xs text-muted-foreground">Emailing you a copy…</p>
              )}
            </div>
          </section>
        )}
      </main>

      <SharedFooter />
    </div>
  );
}
